<script setup lang="ts">
import { getWorker } from '@/services/modules/WorkerService';
import { Ref, computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { formatPriceToFrenchSystem, isAuth } from '@/utils/functions';
import useChatStore from '@/stores/chat';
import { Pro } from '@/models/Pro';
import { usePageContext } from 'vike-vue/usePageContext'
import { navigate } from 'vike/client/router'
import { defineAsyncComponent } from 'vue';
import i18n from '@/localization/i18n';
import RatingStars from '@/views/components/RatingStars.vue';
import RatingBadge from '@/views/components/RatingBadge.vue';
import ProRatingsComponent from './ProRatingsComponent.vue';
// import i18n from '@/localization/i18n';

const DefaultVue = defineAsyncComponent(() => import('@/views/layouts/Default.vue'));
const ImageOff = defineAsyncComponent(() => import('vue-material-design-icons/ImageOff.vue'))
const Phone = defineAsyncComponent(() => import('vue-material-design-icons/Phone.vue'))
const Email = defineAsyncComponent(() => import('vue-material-design-icons/Email.vue'))
const MapMarkerCheckOutline = defineAsyncComponent(() => import('vue-material-design-icons/MapMarkerCheckOutline.vue'))
const CameraOffOutline = defineAsyncComponent(() => import('vue-material-design-icons/CameraOffOutline.vue'))
const MapMarkerAlertOutline = defineAsyncComponent(() => import('vue-material-design-icons/MapMarkerAlertOutline.vue'))
const ArrowLeftBold = defineAsyncComponent(() => import('vue-material-design-icons/ArrowLeftBold.vue'))
const Web = defineAsyncComponent(() => import('vue-material-design-icons/Web.vue'))

const pageContext = usePageContext()

const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL

const { data } = pageContext
const workerData: any = data

const chatpath = ref('/cs/msg_list')
const chatStore = useChatStore()
const hideProfile = ref(false)

// @ts-ignore
const param2 = pageContext.routeParams['*']
const account_id = param2 ? parseInt(param2.replace("/", "")) : 0

const worker: Ref<Pro | null> = ref(workerData.worker ? new Pro(workerData.worker) : null)
const loading = ref(true)
const error = ref(false)

//use the prefetch data in +data.ts file for ssr rendering :
if(workerData.worker){
    try {
        worker.value = new Pro(workerData.worker)

        if (worker.value.profile_visible === true || (worker.value.profile_visible === false && (account_id && worker.value.account_id == account_id))) {
            loading.value = false
        } else {
            loading.value = false
            hideProfile.value = true
        }
    } catch (_) {
        worker.value = null
        error.value = true
        loading.value = false
    }
}

// const t = i18n.global.t
const t = i18n.global.t

onMounted(() => {
    window.scrollTo(0, 0)

    //if worker.value is define it has already its value from +data.ts for ssr rendering
    if(worker.value) return

    loading.value = true
    error.value = false
    hideProfile.value = false

    const payload = {
        "platform_code": pageContext.routeParams?.code
    }

    getWorker(payload)
        .then((response) => {
            worker.value = new Pro(response.data)

            if (worker.value.profile_visible === true || (worker.value.profile_visible === false && (account_id && worker.value.account_id == account_id))) {
                loading.value = false
            } else {
                loading.value = false
                hideProfile.value = true
            }
        })
        .catch((_) => {
            error.value = true
            loading.value = false
        })
})

onBeforeUnmount(() => {
    worker.value = null
})

const modalbutton: Ref<any> = ref(null)
const closemodal: Ref<any> = ref(null)

async function onContact() {
    let conversation = {
        "conversation_id": "conversation_" + worker.value?.account_id,
        "pro": {
            "account_id": worker.value?.account_id,
            "first_name": worker.value?.first_name,
            "last_name": worker.value?.last_name,
            "phone": worker.value?.phone,
            "company_name": worker.value?.company_name,
            "logo": worker.value?.logo,
            "platform_code": worker.value?.platform_code,
            "is_active": worker.value?.is_active
        },
        "last_message": {}
    }

    chatStore.setContactArtisanConvo(conversation)

    if (isAuth()) {
        await navigate(chatpath.value)
    } else {
        modalbutton.value?.click()
    }
}

async function goToLogin() {
    closemodal.value.click()
    sessionStorage.setItem('destination', chatpath.value)
    await navigate('/auth/login')
}

const visibleRef = ref(false)
const indexRef = ref(0)

const showImg = (index: number) => {
    indexRef.value = index
    visibleRef.value = true
}

const onHide = () => visibleRef.value = false;

/**
 * This function is used to generate the completed map url with the pro long, lat , 
 * street and the town from his address.
 * */
function generateMapUrl(address: any) {
    let baseUrl = 'https://maps.google.com/maps?q=';
    let queryString = '';

    if (address.lat && address.long) {
        queryString += address.lat + ',' + address.long;
    }

    if (address.street) {
        queryString += ',' + address.street;
    }

    if (address.town) {
        queryString += ',' + address.town;
    }

    queryString += '&t=&z=13&ie=UTF8&iwloc=&output=embed';

    return baseUrl + queryString;
}

const goBack = async () => {
    try {
        const fromSameOrigin = document && window != undefined && document.referrer && new URL(document.referrer).origin === window.location.origin;
        fromSameOrigin ? window.history.back() : await navigate('/annuaire/search');
    } catch (e: any) {
        await navigate('/annuaire/search');
    }
};

const formated_website_url = computed(() => {
    if (!worker.value || !worker.value?.website_url) {
        return null
    }

    if (worker.value.website_url.startsWith("http")) {
        return worker.value?.website_url
    } else {
        return 'https://' + worker.value?.website_url
    }
})

const get_formated_work_geo_zone_detail_list = computed(() => {
    if (!worker.value || !worker.value.work_geo_zone_detail_list) {
        return []
    }

    return worker.value.work_geo_zone_detail_list.map((zone) => `(${zone.code}) ${zone.name}`).join(', ')
})
</script>

<template>
    <DefaultVue>
        <!-- BEGIN: Content-->
        <div v-if="!loading && worker !== null && !error && !hideProfile"
            class="app-content content p-3 margin-from-header" style="min-height:unset ; margin-left: 0px; ">
            <div class="content-overlay"></div>

            <div class="content-wrapper container-xxl p-0">
                <div class="content-body">
                    <div id="user-profile">
                        <!-- profile header -->
                        <div class="row">
                            <div class="col-12">
                                <div class="card profile-header mb-2">
                                    <!-- profile cover photo Desktop start -->
                                    <div class="worker-cover position-relative d-none d-sm-none d-md-block d-lg-block d-xl-block"
                                        :style="{ backgroundColor: worker.favorite_color }" style="height: 18vh;">

                                        <!-- WEB GOBACK BTN -->
                                        <button
                                            class="btn btn-light mb-1 waves-effect position-absolute d-none d-sm-none d-md-block d-lg-block d-xl-block"
                                            style="top: 15px; right: 15px; color: #FEB23C" @click="goBack">
                                            <arrow-left-bold />
                                        </button>
                                    </div>
                                    <div class="position-relative d-none d-sm-none d-md-block d-lg-block d-xl-block">
                                        <!-- profile picture -->
                                        <div class="profile-img-container d-flex align-items-center">
                                            <div class="profile-img d-none d-sm-none d-md-block d-lg-block d-xl-block"
                                                style="height: 12.5rem;">
                                                <img v-if="worker.logo" :src="worker.logo" style="height: 12.5rem;"
                                                    class="rounded" :alt="worker.company_name" />
                                                <image-off v-else class="icon-override rounded" style="height: 12.5rem;"
                                                    fillColor="#5e5873" />
                                            </div>

                                            <!-- profile title -->
                                            <div class="profile-title ms-3 mb-2 pb-4">
                                                <h1 class="h-style h2-style use-poppins text-white">
                                                    {{ worker.company_name }}
                                                </h1>
                                                <p class="text-white" style="font-size: 16px;">
                                                    {{ worker.specialty }}
                                                </p>
                                                <RatingBadge :rating="worker.rating" :google-rating="worker.google_rating" />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- profile cover photo Desktop end -->

                                    <!-- profile cover photo Mobile start -->
                                    <div class="position-relative d-block d-sm-block d-md-none d-lg-none d-xl-none"
                                        :style="{ backgroundColor: worker.favorite_color }">
                                        <!-- WEB Mobile BTN -->
                                        <button
                                            class="btn btn-light waves-effect position-absolute d-block d-sm-block d-md-none d-lg-none d-xl-none"
                                            style="top: 8px; right: 10px; color: #FEB23C" @click="goBack">
                                            <arrow-left-bold />
                                        </button>

                                        <!-- profile picture -->
                                        <div v-if="worker.logo"
                                            class="mt-4 d-flex align-items-center justify-content-center">
                                            <img :src="worker.logo" style="height: 8.5rem;" class="rounded"
                                                :alt="worker.company_name" />
                                        </div>
                                        <div v-else class="mt-4 d-flex align-items-center justify-content-center">
                                            <div class="rounded" style="background-color: #fff;">
                                                <image-off class="icon-override rounded"
                                                    style="height: 10.5rem !important;" fillColor="#5e5873" />
                                            </div>
                                        </div>

                                        <!-- profile title -->
                                        <div class="profile-title mx-3 mb-2 pb-2 mt-3">
                                            <div class="h-style h2-style use-poppins text-white text-center">
                                                {{ worker.company_name }}
                                            </div>
                                            <p class="text-white text-center" style="font-size: 16px;">
                                                {{ worker.specialty }}
                                            </p>
                                            <div class="d-flex align-items-center justify-content-center">
                                                <RatingBadge :rating="worker.rating" :google-rating="worker.google_rating" />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- profile cover photo Mobile end -->

                                    <div class="card-body mt-5 mt-md-4">
                                        <div class="row">
                                            <div class="col-md-12">
                                                <div class="card-body px-0 px-md-3">
                                                    <div class="row my-md-3">
                                                        <div class="col-md-5 mb-3">
                                                            <div class="h-style h3-style use-poppins card-title">{{
                                                                t('w_txt_wk_comanyInfo') }}</div>
                                                            <p class="card-text text-dark">
                                                                <strong>{{ t('w_txt_wk_legalStatus') }}</strong> <span
                                                                    v-if="worker.company_legal_status">{{
                                                                        worker.company_legal_status }}</span><span v-else
                                                                    class="fw-light fst-italic">{{ t('g_txt_undefined')
                                                                    }}</span><br>
                                                                <strong>{{ t('w_frm_csProposalView_companyCode')
                                                                }}</strong> <span v-if="worker.company_reference">{{
                                                                        worker.formated_company_reference }}</span><span
                                                                    v-else class="fw-light fst-italic">{{
                                                                        t('g_txt_undefined') }}</span><br
                                                                    v-if="worker.company_compl_reference">
                                                                <template v-if="worker.company_compl_reference">
                                                                    <strong>{{ t('w_frm_csProposalView_companyComplRef')
                                                                    }}</strong> {{
                                                                            worker.formated_company_compl_reference }}
                                                                </template>
                                                                <!--<span v-else class="fw-light fst-italic">{{ t('g_txt_undefined') }}</span>--><br>
                                                                <template v-if="worker.company_tax_number">
                                                                    <strong>{{ t('w_txt_csProposalView_tax_number')}}</strong> <span>{{ worker.company_tax_number
                                                                        }}</span><br>
                                                                </template>
                                                                <template v-if="worker.company_activity_code">
                                                                    <strong>{{ t('w_txt_wk_companyActivityCode') }}</strong> <span>{{ worker.company_activity_code }}</span><br>
                                                                </template>
                                                                <span v-if="worker.company_share_capital">
                                                                    <strong>{{ t('w_txt_wk_companyShareCapital')
                                                                    }}</strong> <span>{{
                                                                            formatPriceToFrenchSystem(worker.company_share_capital)
                                                                        }} €<br></span>
                                                                </span>
                                                            </p>

                                                            <div v-if="worker.profile_verified"
                                                                class="card-body ps-0 d-flex align-items-center">
                                                                <img :src="VITE_ASSET_URL + 'assets/public/assets/img/certif/subscribed-badge.webp'"
                                                                    class="badge-img " width="50">
                                                                <p class="ms-1"><strong>{{ t('w_txt_wk_profile_verified') }}</strong></p>
                                                            </div>
                                                            <div v-if="worker.is_affiliated"
                                                                class="card-body ps-0 d-flex align-items-center pt-2">
                                                                <img :src="'https://upload.wikimedia.org/wikipedia/commons/a/a4/Leroy_Merlin_-_logo_%28France%2C_1995-%29.svg'"
                                                                    class="badge-img" width="60">
                                                                <p class="ms-2 mb-0"><strong>{{ t('w_txt_wk_is_affiliated') }}</strong></p>
                                                            </div>

                                                        </div>

                                                        <!-- Start adding -->
                                                        <div
                                                            class="col-12 d-block d-sm-block d-md-none d-lg-none d-xl-none">
                                                            <div class="card card-apply-job ">
                                                                <div class="card-body">
                                                                    <!--                                                        <h5>{{ t('w_txt_wk_profile') }}</h5>-->
                                                                    <div
                                                                        class="d-flex justify-content-between align-items-center mb-1">

                                                                        <div class="card-body p-0">
                                                                            <div
                                                                                class="d-flex justify-content-between align-items-center mb-1">
                                                                                <div class="d-flex flex-row">
                                                                                    <div class="user-info">
                                                                                        <div
                                                                                            class="h-style h3-style use-poppins card-title mt-1">
                                                                                            {{ t('w_fct_header_contact')
                                                                                            }}</div>
                                                                                        <div
                                                                                            class="d-flex flex-row align-items-center">
                                                                                            <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                style="pointer-events: none !important;">
                                                                                                <div
                                                                                                    class="avatar-content">
                                                                                                    <phone
                                                                                                        style="font-size:10px !important;"
                                                                                                        class="d-flex align-items-center justify-content-center no-pointer">
                                                                                                    </phone>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div class="content-body">
                                                                                                <div v-if="worker.phone"
                                                                                                    class="h-style h6-style use-poppins mb-0">
                                                                                                    {{
                                                                                                        worker.formated_phone
                                                                                                    }}</div>
                                                                                                <div v-else
                                                                                                    class="h-style h6-style use-poppins fw-light fst-italic mb-0">
                                                                                                    {{
                                                                                                        t('g_txt_undefined')
                                                                                                    }}</div>
                                                                                            </div>
                                                                                        </div>

                                                                                        <div
                                                                                            class="d-flex flex-row align-items-center mt-2"
                                                                                            v-if="worker.company_email">
                                                                                            <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                style="pointer-events: none !important;">
                                                                                                <div class="avatar-content"
                                                                                                    style="pointer-events: none !important;">
                                                                                                    <email
                                                                                                        class="d-flex align-items-center justify-content-center">
                                                                                                    </email>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div class="content-body">
                                                                                                <div 
                                                                                                    class="h-style h6-style use-poppins mb-0">
                                                                                                    {{
                                                                                                        worker.company_email
                                                                                                    }}</div>
                                                                                                
                                                                                            </div>
                                                                                        </div>

                                                                                        <!-- <div
                                                                                            class="d-flex flex-row align-items-center mt-2">
                                                                                            <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                style="pointer-events: none !important;">
                                                                                                <div class="avatar-content"
                                                                                                    style="pointer-events: none !important;">
                                                                                                    <map-marker-check-outline
                                                                                                        class="d-flex align-items-center justify-content-center"></map-marker-check-outline>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div class="content-body">
                                                                                                <h6 v-if="worker.address"
                                                                                                    class="mb-0">{{
                                                                                                        worker.address.street
                                                                                                        + ', ' +
                                                                                                        worker.address.zipcode
                                                                                                        + ' ' +
                                                                                                        worker.address.town
                                                                                                    }}</h6>
                                                                                                <div v-else
                                                                                                    class="h-style h6-style use-poppins fw-light fst-italic mb-0">
                                                                                                    {{
                                                                                                        t('g_txt_undefined')
                                                                                                    }}</div>
                                                                                            </div>
                                                                                        </div> -->

                                                                                        <div
                                                                                            class="d-flex flex-row align-items-center mt-2"
                                                                                             v-if="worker.website_url">
                                                                                            <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                style="pointer-events: none !important;">
                                                                                                <div class="avatar-content"
                                                                                                    style="pointer-events: none !important;">
                                                                                                    <web
                                                                                                        class="d-flex align-items-center justify-content-center">
                                                                                                    </web>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div class="content-body">
                                                                                                <h6 
                                                                                                    class="mb-0">
                                                                                                    <a style="word-break: break-word;overflow-wrap: break-word;"
                                                                                                        :href="formated_website_url ?? ''"
                                                                                                        target="_blank">
                                                                                                        {{
                                                                                                            worker.website_url
                                                                                                        }}
                                                                                                    </a>
                                                                                                </h6>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>

                                                                                </div>
                                                                            </div>
                                                                            <div class="mt-3">
                                                                                <button type="button" @click="onContact"
                                                                                    class="btn btn-primary w-100 waves-effect"><i
                                                                                        class="bx bx-envelope"></i>{{
                                                                                            t("w_act_sendMessageToPro")
                                                                                        }}</button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div class="mt-2 mb-4 d-flex align-items-center"
                                                                :style="{ backgroundColor: worker.favorite_color }">
                                                                <div class="card-body">
                                                                    <div>
                                                                        <h5 class="text-white "
                                                                            style="text-align: center;">{{
                                                                                t('w_txt_wk_findWorkerWithCode') }}
                                                                        </h5>
                                                                    </div>
                                                                    <div class="h-style h1-style use-poppins text-white border-5 border-white mt-1 platform-code-spacing"
                                                                        style="font-size: 28px; text-align: center;">{{
                                                                            worker.platform_code }}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                        <!-- end adding -->
                                                        <div
                                                            class="col-md-7 d-none d-sm-none d-md-block d-lg-block d-xl-block">
                                                            <div class="row">
                                                                <div class="col-md-8">
                                                                    <div>
                                                                        <div class="card card-apply-job ">
                                                                            <div class="card-body">
                                                                                <div
                                                                                    class="d-flex justify-content-between align-items-center mb-1">

                                                                                    <div class="card-body py-0">
                                                                                        <div
                                                                                            class="d-flex justify-content-between align-items-center mb-1">
                                                                                            <div
                                                                                                class="d-flex flex-row">
                                                                                                <!-- <div class="avatar avatar-lg me-1"
                                                                                                    style="background-color: transparent ;">
                                                                                                    <img v-if="worker.avatar" :src="worker.avatar"
                                                                                                        alt="Avatar" width="60" height="60">
                                                                                                    <span v-else class=""><image-off :size="50" fillColor="#5e5873" /></span>
                                                                                                </div> -->
                                                                                                <div class="user-info">
                                                                                                    <!-- <h5 class="mb-0">{{ t("w_txt_wk_wishToContactPro") }}</h5> -->
                                                                                                    <!-- <p class="card-text mb-0 fw-light">{{ t('w_txt_wk_userName') }} {{ worker.username }}</p> -->
                                                                                                    <!-- <p class="card-text text-dark mb-0">
                                                                                                      {{ t("w_txt_wk_sendMessageDescription") }}
                                                                                                    </p> -->
                                                                                                    <!-- <p v-if="worker.email" class="card-text text-dark mb-0">{{ worker.email }}</p> -->
                                                                                                    <!-- <p v-if="worker.phone" class="card-text text-dark mb-0">{{ formatPhoneNumber(worker.phone) }}</p> -->

                                                                                                    <div
                                                                                                        class="h-style h3-style use-poppins card-title mt-1">
                                                                                                        {{
                                                                                                            t('w_fct_header_contact')
                                                                                                        }}</div>
                                                                                                    <div
                                                                                                        class="d-flex flex-row align-items-center">
                                                                                                        <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                            style="pointer-events: none !important;">
                                                                                                            <div
                                                                                                                class="avatar-content">
                                                                                                                <phone
                                                                                                                    style="font-size:10px !important"
                                                                                                                    class="d-flex align-items-center justify-content-center">
                                                                                                                </phone>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div
                                                                                                            class="content-body">
                                                                                                            <div v-if="worker.phone"
                                                                                                                class="h-style h6-style use-poppins mb-0">
                                                                                                                {{
                                                                                                                    worker.formated_phone
                                                                                                                }}
                                                                                                            </div>
                                                                                                            <div v-else
                                                                                                                class="h-style h6-style use-poppins fw-light fst-italic mb-0">
                                                                                                                {{
                                                                                                                    t('g_txt_undefined')
                                                                                                                }}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>


                                                                                                    <div
                                                                                                        class="d-flex flex-row align-items-center mt-2"
                                                                                                         v-if="worker.company_email">
                                                                                                        <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                            style="pointer-events: none !important;">
                                                                                                            <div
                                                                                                                class="avatar-content">
                                                                                                                <email
                                                                                                                    class="d-flex align-items-center justify-content-center">
                                                                                                                </email>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div
                                                                                                            class="content-body">
                                                                                                            <div 
                                                                                                                class="h-style h6-style use-poppins mb-0">
                                                                                                                {{
                                                                                                                    worker.company_email
                                                                                                                }}</div>
                                                                                                            
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <!-- <div
                                                                                                        class="d-flex flex-row align-items-center mt-2">
                                                                                                        <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                            style="pointer-events: none !important;">
                                                                                                            <div
                                                                                                                class="avatar-content">
                                                                                                                <map-marker-check-outline
                                                                                                                    class="d-flex align-items-center justify-content-center"></map-marker-check-outline>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div
                                                                                                            class="content-body">
                                                                                                            <div v-if="worker.address"
                                                                                                                class="h-style h6-style use-poppins mb-0">
                                                                                                                {{
                                                                                                                    worker.address.street
                                                                                                                    + ', ' +
                                                                                                                    worker.address.zipcode
                                                                                                                    + ' ' +
                                                                                                                    worker.address.town
                                                                                                                }}</div>
                                                                                                            <div v-else
                                                                                                                class="h-style h6-style use-poppins fw-light fst-italic mb-0">
                                                                                                                {{
                                                                                                                    t('g_txt_undefined')
                                                                                                                }}</div>
                                                                                                        </div>
                                                                                                    </div> -->

                                                                                                    <div
                                                                                                        class="d-flex flex-row align-items-center mt-2"
                                                                                                         v-if="worker.website_url">
                                                                                                        <div class="avatar avatar-md bg-light-primary rounded me-2"
                                                                                                            style="pointer-events: none !important;">
                                                                                                            <div
                                                                                                                class="avatar-content">
                                                                                                                <web
                                                                                                                    class="d-flex align-items-center justify-content-center">
                                                                                                                </web>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div
                                                                                                            class="content-body">
                                                                                                            <div 
                                                                                                                class="h-style h6-style use-poppins mb-0">
                                                                                                                <a :href="formated_website_url ?? ''"
                                                                                                                    target="_blank">{{
                                                                                                                        worker.website_url
                                                                                                                    }}</a>
                                                                                                            </div>
                                                                                                            
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div
                                                                                            class="d-flex justify-content-end">
                                                                                            <button type="button"
                                                                                                @click="onContact"
                                                                                                class="btn btn-primary waves-effect on-action-btn">
                                                                                                <i
                                                                                                    class="bx bx-envelope"></i>{{
                                                                                                        t("w_act_sendMessageToPro")
                                                                                                    }}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="col-md-4 d-md-flex d-lg-flex d-xl-flex align-items-center"
                                                                    :style="{ backgroundColor: worker.favorite_color }">
                                                                    <div class="card-body">
                                                                        <div class="pb-2">
                                                                            <div class="h-style h4-style use-poppins text-white "
                                                                                style="text-align: center;">{{
                                                                                    t('w_txt_wk_findWorkerWithCode') }}
                                                                            </div>
                                                                        </div>
                                                                        <div class="h-style h1-style use-poppins text-white border-5 border-white mt-1 platform-code-spacing"
                                                                            style="font-size: 28px; text-align: center;">
                                                                            {{
                                                                                worker.platform_code }}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>



                                                    <div class="row">
                                                        <div class="col-md-5">
                                                            <div class="h-style h3-style use-poppins card-title mt-2">{{
                                                                t('w_txt_wk_companyDescription') }}</div>
                                                            <h3 v-if="worker.description" class="card-text h-as-p"
                                                                style="white-space: pre-line;"
                                                                v-html="worker.formated_description">
                                                            </h3>
                                                            <p v-else class="fw-light fst-italic">{{
                                                                t('g_txt_undefined') }}</p>
                                                        </div>

                                                        <div class="col-md-7 mt-3 mt-md-0">
                                                            <div class="h-style h3-style use-poppins card-title mt-1">{{
                                                                t('w_txt_wk_location') }}</div>

                                                            <p>
                                                                <strong>Zones d’intervention :</strong>&nbsp;
                                                                <span v-if="worker.work_geo_zone_detail_list && worker.work_geo_zone_detail_list.length > 0">
                                                                    {{ get_formated_work_geo_zone_detail_list }}
                                                                </span>
                                                                <span v-else>
                                                                    <i>{{ t('g_txt_undefined') }}</i>
                                                                </span>
                                                            </p>

                                                            <p>
                                                                <strong>Siège social : </strong>
                                                                <span class="h-style h6-style use-poppins" v-if="worker.address">
                                                                    {{ worker.address.street }}, {{ worker.address.zipcode }} {{ worker.address.town }}
                                                                </span>
                                                                <span v-else>
                                                                    <i>{{ t('g_txt_undefined') }}</i>
                                                                </span>
                                                            </p>


                                                            <div class="map-responsive"
                                                                style="border-left: 1px solid #d8d6de;">
                                                                <template v-if="worker.address">
                                                                    <div style="height: 400px;">
                                                                        <div class="mapouter">
                                                                            <div class="gmap_canvas">
                                                                                <iframe width="100%" height="100%"
                                                                                    id="gmap_canvas"
                                                                                    :src="generateMapUrl(worker.address)"
                                                                                    frameborder="0" scrolling="no"
                                                                                    marginheight="0" allowfullscreen
                                                                                    marginwidth="0"></iframe>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </template>
                                                                <div v-else class="text-center pt-5">
                                                                    <div class="h-style h5-style use-poppins fw-light">
                                                                        <span class="mb-4">{{
                                                                            t('w_txt_locationUnavailable') }}</span> <br>
                                                                        <map-marker-alert-outline class="mt-4"
                                                                            :size="56"></map-marker-alert-outline>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <!--/ profile header -->

                        <!-- profile info section -->
                        <section id="profile-info">
                            <div class="row">
                                <!-- left profile info section -->
                                <div class="col-md-6 order-2 order-sm-2 order-md-1 order-lg-1 order-xl-1 mt-4 mt-md-0">
                                    <!-- about -->
                                    <div class="card h-100">
                                        <div class="card-body">
                                            <div class="h-style h3-style use-poppins mb-2">{{ t('w_txt_wk_gallery') }}
                                            </div>
                                            <div class="row">
                                                <template v-if="worker.gallery.length > 0">
                                                    <div v-for="(image, index) in worker.gallery"
                                                        class="col-md-6 col-12 mb-4 profile-latest-img">
                                                        <a href="#" data-toggle="lightbox"
                                                            data-gallery="example-gallery">
                                                            <img :src="image" @click="() => showImg(index)"
                                                                class="rounded gallery-image" />
                                                        </a>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div class="col-md-12 col-12 py-5 d-flex justify-content-center">
                                                        <a href="#">
                                                            <camera-off-outline :size="64" fillColor="#5e5873"
                                                                class="img-fluid rounded" />
                                                        </a>
                                                    </div>
                                                </template>
                                            </div>
                                            <vue-easy-lightbox :visible="visibleRef" :imgs="worker.gallery"
                                                :index="indexRef" @hide="onHide"></vue-easy-lightbox>
                                        </div>
                                        <!-- reload button -->
                                        <div class="row">
                                            <div class="col-12 text-center">
                                                <!-- <button type="button"
                                                    class="btn btn-sm btn-primary block-element border-0 my-3">{{ t("w_act_seeMore") }}</button> -->
                                            </div>
                                        </div>
                                        <!--/ reload button -->
                                    </div>
                                    <!--/ about -->
                                </div>
                                <!--/ left profile info section -->



                                <!-- right profile info section -->
                                <div class="col-md-6 order-1 order-sm-1 order-md-2 order-lg-2 order-xl-2 mt-4 mt-md-0 h-100">
                                    <!-- latest profile pictures -->
                                    <div v-if="(worker.certificate_images.length > 0) || (worker.insurance_warranty !== 'g_lst_work_construct_warranty_type_FR_04_nc')"
                                        class="card mt-3 mt-md-0" style="min-height: 17rem;">
                                        <div class="card-body">
                                            <div class="h-style-with-color h3-style use-poppins mb-75">{{
                                                t('w_txt_csProposalView_complimentaryInfo') }}</div>

                                            <div class="mt-2" v-if="worker.certificate_images.length > 0">
                                                <div class="h-style-with-color h4-style use-poppins mb-75">{{
                                                    t('w_txt_csProposalView_certificates') }}</div>
                                                <div class="avatar-xl">
                                                    <template v-for="image in worker.certificate_images">
                                                        <img :src="image" height="150" />
                                                    </template>
                                                </div>
                                            </div>
                                            <!-- <div v-else class="mt-2">
                                                <div class="h-style-with-color h4-style use-poppins mb-75">{{ t('w_txt_csProposalView_certificates') }}</div>
                                                <p class="fw-light fst-italic">{{ t('g_txt_undefinedPlural') }}</p>
                                            </div> -->
                                            <template
                                                v-if="(worker.insurance_warranty !== 'g_lst_work_construct_warranty_type_FR_04_nc')">
                                                <div class="mt-2"
                                                    v-if="worker.insurance_name || worker.insurance_policy_number || worker.insurance_geo_coverage">
                                                    <div class="h-style h4-style use-poppins">{{
                                                        t('w_txt_csProposalView_insurance_policy') }}
                                                    </div>
                                                    <strong>{{ t('w_frm_csProposalView_insurer_name')}}</strong>&nbsp;<span v-if="worker.insurance_name">{{ worker.insurance_name }}</span> <span v-else class="fw-light fst-italic">{{ t('g_txt_undefined') }}</span>
                                                    <br>
                                                    <strong>{{ t('w_frm_csProposalView_insurance_warranty')
                                                    }}</strong>&nbsp;<span v-if="worker.insurance_warranty">{{
                                                            worker.insurance_warranty ? t(worker.insurance_warranty) : ''
                                                        }}</span> <span v-else class="fw-light fst-italic">{{
                                                            t('g_txt_undefined') }}</span> <br>
                                                    <strong>{{ t('w_frm_csProposalView_insurance_geo_area')
                                                    }}</strong>&nbsp;<span v-if="worker.insurance_geo_coverage">{{
                                                            worker.insurance_geo_coverage }}</span> <span v-else
                                                        class="fw-light fst-italic">{{ t('g_txt_undefined') }}</span>
                                                </div>
                                                <div v-else class="mt-2">
                                                    <div class="h-style h4-style use-poppins">{{
                                                        t('w_txt_csProposalView_insurance_policy') }}</div>
                                                    <span class="fw-light fst-italic">{{ t('g_txt_undefinedFeminine')
                                                    }}</span>
                                                </div>
                                            </template>

                                            <!--                                            <div v-if="worker.website_url" class="mt-2">-->
                                            <!--                                                <h4 class="mb-75">{{ t('w_txt_website') }}</h4>-->
                                            <!--                                                <p class="card-text">{{ worker.website_url ?? '' }}</p>-->
                                            <!--                                            </div>-->

                                        </div>
                                    </div>
                                    <!--/ latest profile pictures -->

                                    <ProRatingsComponent v-if="worker.account_id" :account_id="worker.account_id" class="mt-3" />
                                </div>
                                <!--/ right profile info section -->

                            </div>

                        </section>
                        <!--/ profile info section -->


                    </div>
                </div>
            </div>
        </div>
        <div v-show="loading && !error && !hideProfile"
            style="height: 90vh; display: flex; justify-content: center; align-items: center;">
            <div>
                <vue-loaders-ball-spin-fade-loader color="#FEB23C" scale="1"></vue-loaders-ball-spin-fade-loader>
            </div>
        </div>
        <div v-show="!loading && (error || hideProfile)" class="content-wrapper container-xxl p-0"
            style="margin-top: 100px;">
            <div class="content-body">

                <div class="auth-wrapper auth-basic" style="min-height: calc(var(--vh,1vh)*80);">
                    <div class="w-100 text-center ">
                        <img :src="VITE_ASSET_URL + 'assets/user/assets/img/404.webp'" width="500">
                        <div class="h-style-with-color h1-style use-poppins" style="font-size: 80px;">{{
                            t('w_msg_404_oops') }}
                        </div>
                        <div class="h-style-with-color h3-style use-poppins">{{ t('w_msg_404_page_error') }}</div>
                        <div class="h-style-with-color h3-style use-poppins">{{ t('w_msg_404_page_message') }}</div>
                    </div>
                </div>

            </div>
        </div>
        <!-- END: Content-->


        <button ref="modalbutton" v-show="false" data-bs-toggle="modal" data-bs-target="#verifymobile"></button>
        <div ref="mobileVerificationModalRef" class="modal fade" id="verifymobile" tabindex="-1"
            aria-labelledby="twoFactorAuthSmsTitle" style="display: none;" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg two-factor-auth-sms">
                <div class="modal-content">
                    <div class="modal-header bg-transparent" style="border-bottom:none;padding-bottom:0px;">
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="text-center modal-body pb-3 px-sm-5 mx-50">
                        <div class="h-style h4-style use-poppins">{{ t('w_txt_wk_loginAlertHeader') }}</div>
                        <div>
                            <p>{{ t('w_msg_wk_loginAlertMessage') }}</p>
                            <div class="my-5">
                                <button ref="closemodal" data-bs-dismiss="modal" aria-label="Close"
                                    class="btn btn-outline-secondary me-2 on-see-profile-action-btn-modal ">{{
                                        t('w_act_cancel')
                                    }}</button>
                                <button @click="goToLogin" class="btn btn-primary on-see-profile-action-btn-modal">{{
                                    t('w_act_wk_loginAlertMessage') }}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </DefaultVue>
</template>

<style scoped>
@import url('@/assets/css/page-profile.css');
@import url('@/assets/css/ext-component-ratings.css');
@import url('@/assets/css/style-rtl.css');

.h-as-p {
    font-weight: normal !important;
    font-size: 14px;
    line-height: 1.5rem;
    font-family: "Century Gothic", sans-serif !important;
}

.on-see-profile-action-btn-modal {
    padding: 10px 15px !important;
    font-size: 14px !important;
    width: 180px !important;
}

.blur {
    -webkit-filter: blur(2px);
    -moz-filter: blur(2px);
    -o-filter: blur(2px);
    -ms-filter: blur(2px);
    filter: blur(2px);
}

.platform-code-spacing {
    letter-spacing: 4px;
}

.gallery-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
}

.icon-override :deep(svg) {
    width: 100% !important;
    height: 100% !important;
}

@media only screen and (max-width: 600px) {
    .worker-cover {
        height: 20vh !important;
    }
}
</style>